import { CheckIcon, Tag } from "@stacklet/ui";
import { graphql, useFragment } from "react-relay";

import { cloudOrganizationPath } from "./constants";
import { IdentityProviderCard } from "./IdentityProviderCard";

import type { CloudOrganizationCard_organization$key } from "./__generated__/CloudOrganizationCard_organization.graphql";

/** Props for the CloudOrganizationCard component */
interface Props {
  /** Fragment reference for the cloud organization the card describes */
  organizationRef: CloudOrganizationCard_organization$key;
}

/**
 * A cloud organization acting as an identity provider, listed as a card.
 *
 * There is no "SSO Login" capability: an organization is a source of identities
 * rather than a way to sign in. What it offers instead is sync, and only the
 * options it actually enables are tagged.
 */
export function CloudOrganizationCard({ organizationRef }: Props) {
  const organization = useFragment(
    graphql`
      fragment CloudOrganizationCard_organization on CloudOrganization {
        idpNamespace
        idpType
        key
        name
        provider
        syncOptions {
          syncGroups
          syncResourceViewerRoles
        }
      }
    `,
    organizationRef,
  );

  return (
    <IdentityProviderCard
      detailsPath={cloudOrganizationPath(
        organization.provider,
        organization.key,
      )}
      identifier={organization.key}
      idpType={organization.idpType}
      namespace={organization.idpNamespace}
      tags={[
        organization.syncOptions.syncGroups ? (
          <Tag
            color="green"
            icon={<CheckIcon size={14} />}
            id="sync-groups"
            key="sync-groups"
          >
            Group Sync
          </Tag>
        ) : null,
        organization.syncOptions.syncResourceViewerRoles ? (
          <Tag
            color="green"
            icon={<CheckIcon size={14} />}
            id="sync-roles"
            key="sync-roles"
          >
            Role Assignment Sync
          </Tag>
        ) : null,
      ]}
      title={organization.name}
    />
  );
}
